import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet";
import { BRAND, NAV_LINKS } from "@/lib/constants";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100"
          : "bg-white"
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link
          to="/"
          data-testid="logo-link"
          className="font-heading text-xl md:text-2xl font-bold text-[#20611B] tracking-tight"
        >
          {BRAND.name}
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {NAV_LINKS.map((link) => {
            const isActive = location.pathname === link.href;
            return (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium transition-colors ${
                  isActive
                    ? "text-[#20611B] font-semibold"
                    : "text-[#1A1A1A] hover:text-[#20611B]"
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden md:block">
          <Link to={BRAND.signupPath}>
            {/* Stripe checkout will connect here */}
            <Button
              data-testid="header-cta"
              className="bg-[#D4A84B] hover:bg-[#B88E3B] text-white font-bold h-10 px-6 rounded-md shadow-sm hover:shadow-md transition-all active:scale-95 text-sm tracking-wide"
            >
              Start Free Trial
            </Button>
          </Link>
        </div>

        {/* Mobile hamburger */}
        <div className="md:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="mobile-menu-trigger"
                aria-label="Open menu"
              >
                <Menu className="h-6 w-6 text-[#1A1A1A]" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 bg-white">
              <SheetTitle className="font-heading text-lg font-bold text-[#20611B] mb-8">
                {BRAND.name}
              </SheetTitle>
              <nav className="flex flex-col gap-4" data-testid="mobile-nav">
                {NAV_LINKS.map((link) => {
                  const isActive = location.pathname === link.href;
                  return (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setOpen(false)}
                      className={`text-left text-base font-medium py-2 transition-colors ${
                        isActive
                          ? "text-[#20611B] font-semibold"
                          : "text-[#1A1A1A] hover:text-[#20611B]"
                      }`}
                      data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                    >
                      {link.label}
                    </Link>
                  );
                })}
                <Link to={BRAND.signupPath} onClick={() => setOpen(false)}>
                  <Button
                    data-testid="mobile-cta"
                    className="w-full bg-[#D4A84B] hover:bg-[#B88E3B] text-white font-bold h-12 rounded-md mt-4 text-sm tracking-wide"
                  >
                    Start Free Trial
                  </Button>
                </Link>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
